/**
 * Sticker-pack publishing contract: Telegram short-name rules, display-title
 * branding, ownership model, and the publish-job progress shape.
 *
 * These are pure helpers shared by the backend engine, the Mini App and tests.
 */

/** Telegram sticker-set short names are capped at 64 chars (incl. the bot suffix). */
import type { Plan } from './policy.js';
import type { Role } from './roles.js';

export const STICKER_NAME_MAX = 64;

/**
 * Max stickers accepted by a single `createNewStickerSet` call. Verified against
 * the Bot API; larger packs create with this batch, then `addStickerToSet` the
 * remainder. We do not invent a stricter cap.
 */
export const MAX_INITIAL_STICKERS = 50;

/** The mandatory Telegram suffix for bot-created sets: `_by_<bot_username>`. */
export function botNameSuffix(botUsername: string): string {
  return `_by_${botUsername}`;
}

export type ShortNameError = 'empty' | 'start' | 'chars' | 'underscore' | 'tooLong';

export interface ShortNameResult {
  ok: boolean;
  /** The full Telegram short name `<prefix>_by_<bot>` when valid. */
  finalName?: string;
  error?: ShortNameError;
}

/**
 * Build + validate the final Telegram short name from the user-chosen prefix.
 * The user edits ONLY the prefix; the `_by_<bot>` suffix is always appended.
 * Enforces Telegram's real rules: begins with a letter, only English letters /
 * digits / underscores, no consecutive underscores, does not end with an
 * underscore, and the final name fits within {@link STICKER_NAME_MAX}.
 */
export function buildShortName(prefix: string, botUsername: string): ShortNameResult {
  const p = prefix.trim();
  if (p.length === 0) return { ok: false, error: 'empty' };
  if (!/^[a-zA-Z]/.test(p)) return { ok: false, error: 'start' };
  if (!/^[a-zA-Z0-9_]+$/.test(p)) return { ok: false, error: 'chars' };
  if (/__/.test(p) || p.endsWith('_')) return { ok: false, error: 'underscore' };
  const finalName = `${p}${botNameSuffix(botUsername)}`;
  if (finalName.length > STICKER_NAME_MAX) return { ok: false, error: 'tooLong' };
  return { ok: true, finalName };
}

/** Slugify a human title into a suggested, likely-valid short-name prefix. */
export function suggestShortNamePrefix(title: string, botUsername = ''): string {
  let s = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!/^[a-z]/.test(s)) s = `pack_${s}`.replace(/_+$/g, '');
  if (s.length === 0) s = 'pack';
  // Keep room for the `_by_<bot>` suffix within the 64-char limit.
  const room = Math.max(4, STICKER_NAME_MAX - botNameSuffix(botUsername).length);
  return s.slice(0, Math.min(48, room));
}

// ---- Display-title branding -----------------------------------------------
/**
 * Telegram's HUMAN-READABLE sticker set title limit. Deliberately a separate
 * constant from {@link STICKER_NAME_MAX}: that one bounds the technical
 * name/slug, which may only contain `[A-Za-z0-9_]`. The decorative brand below
 * belongs to the title ONLY and must never reach a slug.
 */
export const PACK_TITLE_MAX = 64;

/**
 * The sticker-pack branding tag applied to a pack's DISPLAY title.
 *
 * This is the STICKER BOT handle, deliberately distinct from the main Astral
 * channel (@AstralAtelier) referenced elsewhere in the app: a pack title points
 * at the bot that made it, not at the channel.
 */
export const PACK_TITLE_BRAND = '﹙@AstralStickerBot﹚';

/**
 * Truncate by CODE POINT, not UTF-16 unit, so an emoji or Persian cluster can
 * never be sliced in half, and drop any trailing joiner/variation selector that
 * would otherwise leave a dangling partial glyph.
 */
function clampCodePoints(value: string, max: number): string {
  const points = Array.from(value);
  if (points.length <= max) return value;
  let out = points.slice(0, Math.max(0, max)).join('');
  // U+200D ZWJ, U+FE0E/U+FE0F variation selectors, and combining marks.
  out = out.replace(/[‍︎️̀-ͯ]+$/u, '');
  return out;
}

/** Clamp a display title to Telegram's limit without breaking a character. */
export function clampPackTitle(title: string): string {
  return clampCodePoints(title.trim(), PACK_TITLE_MAX);
}

/**
 * Append the Astral branding tag to a pack's display title.
 *
 * Idempotent (never doubles on edit/retry/resume), and the COMPLETE tag always
 * survives: when the combined length would exceed Telegram's limit, only the
 * user's own title is shortened — the tag is never partially cut or silently
 * dropped. Shortening is code-point safe, so Persian and emoji titles stay valid.
 */
export function brandPackTitle(title: string): string {
  const base = title.trim();
  if (base.endsWith(PACK_TITLE_BRAND)) return clampPackTitle(base);
  const suffix = ` ${PACK_TITLE_BRAND}`;
  const room = PACK_TITLE_MAX - Array.from(suffix).length;
  const head = clampCodePoints(base, Math.max(0, room)).trim();
  return `${head}${suffix}`;
}

// ---- Branding placement ----------------------------------------------------
/**
 * Where the Astral tag sits in a pack's DISPLAY title.
 *
 * Applies to the human-readable title only — never to the technical short name,
 * which may contain nothing but `[A-Za-z0-9_]`.
 */
export const PACK_BRANDING_PLACEMENTS = ['NONE', 'START', 'END'] as const;
export type PackBrandingPlacement = (typeof PACK_BRANDING_PLACEMENTS)[number];

/**
 * The safe fallback: an ineligible account, and any stored value that is
 * present but unrecognised, always resolves here. Corrupt data must never
 * silently drop the tag.
 */
export const DEFAULT_PACK_BRANDING: PackBrandingPlacement = 'END';

/**
 * What an ELIGIBLE account gets when the project has NO stored choice.
 *
 * Backward compatibility: before placement existed, active Premium packs
 * published untagged. Every project created then has no `packBranding` field,
 * so defaulting those to END would silently re-brand packs a paying user
 * already published without a tag. "No field" therefore means "as before" —
 * untagged — until the user explicitly picks START or END.
 */
export const ELIGIBLE_DEFAULT_PACK_BRANDING: PackBrandingPlacement = 'NONE';

export function isPackBrandingPlacement(value: unknown): value is PackBrandingPlacement {
  return typeof value === 'string' && (PACK_BRANDING_PLACEMENTS as readonly string[]).includes(value);
}

/**
 * Who may choose a placement.
 *
 * Role and plan are INDEPENDENT dimensions and both grant this on their own:
 * ADMIN/OWNER qualify by role whatever their subscription, and a plain USER
 * qualifies by holding active Premium. Deliberately NOT expressed via any
 * admin permission — customising a title is a product perk, not privileged
 * access, and tying the two would let a product change leak into authorization.
 */
export function canCustomizePackBranding(role: Role, plan: Plan): boolean {
  return role === 'ADMIN' || role === 'OWNER' || plan === 'PREMIUM' || plan === 'PREMIUM_PLUS';
}

/**
 * THE resolver for effective branding. Frontend and backend both call this, so
 * the rule cannot drift between them.
 *
 * An ineligible user always resolves to END regardless of what is stored —
 * which covers a Free client posting `NONE`, a stale preference left behind by
 * an expired subscription, and hand-edited project data alike. The STORED value
 * is never rewritten here: if entitlement returns, the original choice simply
 * becomes effective again.
 *
 * For an ELIGIBLE user the three cases are deliberately distinct:
 *   - a recognised choice          → honoured exactly
 *   - ABSENT (undefined/null)      → NONE, preserving pre-feature behaviour for
 *                                    projects that predate the field
 *   - present but UNRECOGNISED     → END, the safe fallback for corrupt data
 */
export function resolvePackBranding(
  role: Role,
  plan: Plan,
  stored: unknown,
): PackBrandingPlacement {
  // Entitlement is checked first, so no stored value of any shape can bypass it.
  if (!canCustomizePackBranding(role, plan)) return DEFAULT_PACK_BRANDING;
  if (stored === undefined || stored === null) return ELIGIBLE_DEFAULT_PACK_BRANDING;
  return isPackBrandingPlacement(stored) ? stored : DEFAULT_PACK_BRANDING;
}

/**
 * The retired branding token, kept ONLY so already-published packs can be
 * recognised and migrated.
 *
 * It was emitted exclusively by Astral, in these decorative parentheses, so
 * matching it is unambiguous. A plain `@AstralAtelier` mention — the main
 * channel, in user text or anywhere else — has no parentheses and is never
 * touched by anything in this file.
 */
export const LEGACY_PACK_TITLE_BRAND = '﹙@AstralAtelier﹚';

/**
 * Tokens recognised as OUR branding when normalising a title.
 *
 * Includes the retired tag so a republish cannot stack the old and the new one
 * on the same pack.
 */
const PACK_TITLE_BRAND_TOKENS = [PACK_TITLE_BRAND, LEGACY_PACK_TITLE_BRAND] as const;

/**
 * Remove the exact Astral tag from either end of a title.
 *
 * Scoped to the known tokens — arbitrary user text is never touched. This is
 * what makes the formatter idempotent across re-publish, resume and a placement
 * change, and what normalises titles saved under the old append-only behaviour.
 */
export function stripPackBrand(title: string): string {
  let out = title.trim();
  let changed = true;
  while (changed) {
    changed = false;
    for (const token of PACK_TITLE_BRAND_TOKENS) {
      if (out.startsWith(token)) {
        out = out.slice(token.length).trim();
        changed = true;
      }
      if (out.endsWith(token)) {
        out = out.slice(0, out.length - token.length).trim();
        changed = true;
      }
    }
  }
  return out;
}

/**
 * THE title formatter: base title + placement → final Telegram display title.
 *
 * Always strips any existing tag first, so the tag can never be duplicated or
 * stranded on the wrong side when the placement changes. When the result would
 * exceed Telegram's limit only the USER's portion is shortened — the tag is
 * never partially cut, and shortening is code-point safe so Persian clusters
 * and emoji stay intact.
 */
export function formatPackTitle(title: string, placement: PackBrandingPlacement): string {
  const base = stripPackBrand(title);
  if (placement === 'NONE') return clampPackTitle(base);

  const affix = ` ${PACK_TITLE_BRAND}`;
  const room = Math.max(0, PACK_TITLE_MAX - Array.from(affix).length);
  const head = clampCodePoints(base, room).trim();
  return placement === 'START' ? `${PACK_TITLE_BRAND} ${head}`.trim() : `${head}${affix}`.trim();
}

/**
 * The display title to publish under, given whether the creator holds active
 * Premium. Retained for callers that only know the boolean; both map onto the
 * placement model — Premium means "no tag", Free means the tag at the end.
 */
export function packTitleFor(title: string, hasActivePremium: boolean): string {
  return formatPackTitle(title, hasActivePremium ? 'NONE' : 'END');
}

// ---- Published-title reconciliation (branding migration) -------------------
/**
 * Where a given token sits in a title, if at all. Ends only — the tag has never
 * been emitted mid-title, and a mid-title match is user text, not our branding.
 */
function placementOfToken(title: string, token: string): PackBrandingPlacement | null {
  const value = title.trim();
  if (value.startsWith(token)) return 'START';
  if (value.endsWith(token)) return 'END';
  return null;
}

export interface PackTitleBrandState {
  /** Where Astral branding sits in this title (NONE = no Astral tag at all). */
  placement: PackBrandingPlacement;
  /** The title carries the RETIRED token and therefore needs migrating. */
  legacy: boolean;
  /** The title already carries the CURRENT token. */
  current: boolean;
}

/**
 * Read the branding out of a REMOTE Telegram pack title.
 *
 * The remote title is the only record of how a published pack is actually
 * branded: `Project.title` stores the user's own base title, unbranded, and the
 * tag is applied on the way to Telegram. Migration therefore reads from
 * Telegram, not from the database.
 */
export function detectPackTitleBrand(title: string): PackTitleBrandState {
  const current = placementOfToken(title, PACK_TITLE_BRAND);
  const legacy = placementOfToken(title, LEGACY_PACK_TITLE_BRAND);
  return {
    // The CURRENT token wins when both are present (a half-finished migration):
    // it is the newer intent, and the stale one is stripped either way.
    placement: current ?? legacy ?? 'NONE',
    legacy: legacy !== null,
    current: current !== null,
  };
}

export const PACK_TITLE_RECONCILE_REASONS = [
  /** Nothing to do: no Astral branding, and none is required. */
  'NO_BRANDING',
  /** Already carries the current token — left exactly as it is. */
  'ALREADY_CURRENT',
  /** Retired token found: swapped for the current one, placement preserved. */
  'MIGRATED_TOKEN',
  /** Untagged pack whose owner's resolved entitlement requires a tag. */
  'APPLIED_BRANDING',
] as const;
export type PackTitleReconcileReason = (typeof PACK_TITLE_RECONCILE_REASONS)[number];

export interface PackTitleReconciliation {
  /** The title the remote set should carry. */
  title: string;
  /** Whether a Telegram write is warranted at all. */
  changed: boolean;
  reason: PackTitleReconcileReason;
  placement: PackBrandingPlacement;
  /**
   * The base title had to lose characters to fit Telegram's 64-char limit. Only
   * possible on migration, because the new token is 3 code points longer than
   * the retired one, so a title that was previously exactly at the limit no
   * longer fits. The TAG is never cut — only the user's own text, and only by
   * the minimum, code-point safe.
   */
  trimmed: boolean;
}

/**
 * Decide what an ALREADY-PUBLISHED pack's remote title should be.
 *
 * Deliberately conservative — it answers "does this pack need a write?" and
 * defaults to no:
 *   - retired token present  → swap it for the current one, KEEPING placement
 *                              (START stays START, END stays END) and the
 *                              user's base title verbatim
 *   - current token present  → nothing (never moved, never duplicated)
 *   - no token at all        → nothing, UNLESS `resolved` is a placement that
 *                              entitlement requires, which only an explicit,
 *                              user-initiated resync passes in
 *
 * `resolved` must come from {@link resolvePackBranding} — the caller's role and
 * plan — never from client input. Pass `null` for a pure token migration that
 * cannot brand anything that was not already branded.
 */
export function reconcilePackTitleBranding(
  remoteTitle: string,
  resolved: PackBrandingPlacement | null = null,
): PackTitleReconciliation {
  const state = detectPackTitleBrand(remoteTitle);
  const base = stripPackBrand(remoteTitle);

  let placement: PackBrandingPlacement = state.placement;
  let reason: PackTitleReconcileReason;
  if (state.legacy) {
    reason = 'MIGRATED_TOKEN';
  } else if (state.current) {
    reason = 'ALREADY_CURRENT';
  } else if (resolved && resolved !== 'NONE') {
    placement = resolved;
    reason = 'APPLIED_BRANDING';
  } else {
    placement = 'NONE';
    reason = 'NO_BRANDING';
  }

  const title = formatPackTitle(base, placement);
  // A no-op reason never produces a write, so a cosmetic difference (stray
  // whitespace Telegram already stores) can never trigger a pointless rename.
  const changed =
    (reason === 'MIGRATED_TOKEN' || reason === 'APPLIED_BRANDING') && title !== remoteTitle;
  return {
    title,
    changed,
    reason,
    placement,
    trimmed: Array.from(stripPackBrand(title)).length < Array.from(base).length,
  };
}

// ---- Ownership model ------------------------------------------------------
/**
 * Who owns the Telegram sticker set:
 *  - LEGACY_USER_OWNED : created under the old model (the end user is the owner).
 *  - ASTRAL_MANAGED    : owned by the configured Astral account; the end user
 *                        manages it only through Astral (verified supported).
 */
export const OWNERSHIP_MODES = ['LEGACY_USER_OWNED', 'ASTRAL_MANAGED'] as const;
export type OwnershipMode = (typeof OWNERSHIP_MODES)[number];

// ---- Publish job / progress -----------------------------------------------
export const PUBLISH_STATES = [
  'PREPARING',
  'CREATING_SET',
  'UPLOADING',
  'FINALIZING',
  'COMPLETED',
  'FAILED',
] as const;
export type PublishState = (typeof PUBLISH_STATES)[number];

/**
 * Durable lifecycle of a publish job, persisted so a backend restart cannot
 * strand the Mini App polling a job that died with the process.
 *
 *   QUEUED    — row created; Telegram work has not begun.
 *   RUNNING   — Telegram work in progress; `phase` carries the detail.
 *   SUCCEEDED — pack published (terminal).
 *   FAILED    — publish did not complete (terminal).
 *
 * Legal transitions are ONLY QUEUED→RUNNING, QUEUED→FAILED, RUNNING→SUCCEEDED
 * and RUNNING→FAILED. Terminal states are never reopened; a retry creates a new
 * job rather than resurrecting an old one.
 */
export const PUBLISH_JOB_STATUSES = ['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED'] as const;
export type PublishJobStatus = (typeof PUBLISH_JOB_STATUSES)[number];

/** Statuses that are still in flight (a client should keep polling). */
export const ACTIVE_PUBLISH_JOB_STATUSES: PublishJobStatus[] = ['QUEUED', 'RUNNING'];

/** Statuses that will never change again (a client must stop polling). */
export function isTerminalPublishState(state: PublishState): boolean {
  return state === 'COMPLETED' || state === 'FAILED';
}

/**
 * Stable machine-readable failure reasons.
 *
 * PUBLISH_INTERRUPTED means the backend died mid-publish. It is reported as a
 * plain failure so the user gets the existing retry flow: replaying the
 * Telegram create/add calls automatically cannot be proven idempotent, so the
 * job is failed rather than resumed behind the user's back.
 */
export const PUBLISH_ERROR_CODES = ['PUBLISH_FAILED', 'PUBLISH_INTERRUPTED'] as const;
export type PublishErrorCode = (typeof PUBLISH_ERROR_CODES)[number];

export interface PublishJobResult {
  url: string;
  name: string;
  count: number;
}

/** Observable progress of a publish job (polled by the Mini App). */
export interface PublishJobDTO {
  id: string;
  projectId: string;
  state: PublishState;
  totalStickers: number;
  processedStickers: number;
  /** 0–100, derived from processed/total and phase. */
  percentage: number;
  error: string | null;
  /** Stable failure reason when `state` is FAILED (else null). */
  errorCode: PublishErrorCode | null;
  result: PublishJobResult | null;
}
